/**
 * API Configuration
 * Centralizes all API URL configuration for the application
 */

// API Versions
export const API_VERSION_V2 = 'v2';

// Helper functions to create dynamic endpoints
export function createProcessEndpoint(id: string): string {
  return `${API_VERSION_V2}/imports/${id}/process`;
}

export function createGetSalesEndpoint(id: string): string {
  return `${API_VERSION_V2}/imports/${id}/sales`;
}

export function createGetAdspendEndpoint(id: string): string {
  return `${API_VERSION_V2}/imports/${id}/adspend`;
}

export function createValidateEndpoint(id: string): string {
  return `${API_VERSION_V2}/imports/${id}/validate`;
}

export function createUploadEndpoint(type: string): string {
  // Use the type-specific import endpoints that match the backend routes
  if (type === 'sales') {
    return `${API_VERSION_V2}/imports/sales`;
  } else if (type === 'adspend') {
    return `${API_VERSION_V2}/imports/adspend`;
  } else {
    return `${API_VERSION_V2}/imports`;
  }
}

export function createStreamingProcessEndpoint(id: string): string {
  return `${API_VERSION_V2}/streaming-imports/process/${id}`;
}

export function createStatusEndpoint(id: string): string {
  return `${API_VERSION_V2}/streaming-imports/status/${id}`;
}

export function createValidationEndpoint(id: string): string {
  return `${API_VERSION_V2}/streaming-imports/validation/${id}`;
}

export function createCancelEndpoint(id: string): string {
  return `${API_VERSION_V2}/streaming-imports/cancel/${id}`;
}

export function createStreamingResultsEndpoint(id: string): string {
  return `${API_VERSION_V2}/streaming-imports/results/${id}`;
}

export function createStreamingMapEndpoint(id: string): string {
  return `${API_VERSION_V2}/streaming-imports/map/${id}`;
}

// Report-specific helper functions
export function createGetReportEndpoint(id: string): string {
  return `${API_VERSION_V2}/reports/${id}`;
}

export function createExportReportEndpoint(id: string, format: string): string {
  return `${API_VERSION_V2}/reports/export/${id}?format=${format}`;
}

export function createFilteredReportEndpoint(filters: string): string {
  return `${API_VERSION_V2}/reports?${filters}`;
}

// Endpoints
export const ENDPOINTS = {
  // Auth endpoints - These do NOT use the v2 version prefix
  AUTH: {
    LOGIN: 'auth/login', // maps to /api/auth/login (no v2)
    REGISTER: 'auth/register',
    PROFILE: 'auth/profile',
    REFRESH: 'auth/refresh',
    LOGOUT: 'auth/logout',
  },

  // Import endpoints - These DO use the v2 version prefix
  IMPORTS: {
    SALES: `${API_VERSION_V2}/imports/sales`, // maps to /api/v2/imports/sales
    ADSPEND: `${API_VERSION_V2}/imports/adspend`, // maps to /api/v2/imports/adspend
    UPLOAD: `${API_VERSION_V2}/imports`, // Generic upload endpoint
    PROCESS: createProcessEndpoint,
    GET_SALES: createGetSalesEndpoint,
    GET_ADSPEND: createGetAdspendEndpoint,
    VALIDATE: createValidateEndpoint,
    SESSIONS: `${API_VERSION_V2}/imports`, // List all imports
    STATUS: (id: string) => `${API_VERSION_V2}/imports/${id}/status`,
    PREVIEW: (id: string) => `${API_VERSION_V2}/imports/${id}/preview`,
  },

  // Streaming import endpoints for large files
  STREAMING_IMPORTS: {
    UPLOAD: createUploadEndpoint,
    PROCESS: createStreamingProcessEndpoint,
    STATUS: createStatusEndpoint,
    VALIDATION: createValidationEndpoint,
    CANCEL: createCancelEndpoint,
    RESULTS: createStreamingResultsEndpoint,
    MAP: createStreamingMapEndpoint,
  },

  // Report endpoints
  REPORTS: {
    LIST: `${API_VERSION_V2}/reports`, // maps to /api/v2/reports
    GENERATE: `${API_VERSION_V2}/reports/generate`,
    GET: createGetReportEndpoint,
    DELETE: createGetReportEndpoint, // Same structure as GET but will use DELETE method
    EXPORT: createExportReportEndpoint,
    FILTERED: createFilteredReportEndpoint,
    SAVED_VIEWS: `${API_VERSION_V2}/reports/saved-views`,
  },
};